/* ============================================================
   ET - HEADER — mega panel + mobile drawer behaviour.

   Deliberately standalone: it does NOT extend Impact's DialogElement.
   Impact's drawer renders through a shadow-DOM shell (#drawer-default-template)
   whose chrome does not match this design, and it appends itself to <body> on
   open — which breaks any section-scoped selector and requires bespoke editor
   teardown. Owning the markup is simpler here and cannot regress the cart or
   search drawers.

   The one thing we must NOT reuse is Impact's `.lock` scroll lock: it is
   driven by a PRIVATE STATIC ref-counter inside DialogElement, so adding or
   removing that class by hand desynchronises the counter and the next cart
   drawer close would strip our lock. This file locks with its own class
   (see .et-header-scroll-lock in et-header.css), which composes safely — if
   both are active, both hold, and each releases only its own.
   ============================================================ */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-et-header-root]';

  /* Keep in sync with the desktop/drawer breakpoint in et-header.css (1000px),
     which is itself pinned to Impact's `md` breakpoint - see the note there. */
  var DESKTOP_QUERY = '(min-width: 1000px)';
  var HOVER_QUERY = '(hover: hover) and (pointer: fine)';

  var OPEN_DELAY = 90; /* hover intent before a panel opens */
  var CLOSE_DELAY = 200; /* grace period while the pointer travels trigger -> panel */
  var TRANSITION_MS = 320; /* fallback when transitionend never fires (display:none, reduced motion) */

  var SCROLL_LOCK_CLASS = 'et-header-scroll-lock';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
    ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // ----------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------

  function roots(scope) {
    var node = scope || document;
    var found = [];
    /* shopify:section:load hands over the SECTION WRAPPER, not the root, so the
       scope node itself has to be tested as well as its descendants. */
    if (node.matches && node.matches(ROOT_SELECTOR)) found.push(node);
    Array.prototype.forEach.call(node.querySelectorAll(ROOT_SELECTOR), function (el) {
      if (found.indexOf(el) === -1) found.push(el);
    });
    return found;
  }

  function isDesktop() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  function canHover() {
    return window.matchMedia(HOVER_QUERY).matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Reveal an element that is display:none via [hidden], then transition it in.
     The forced reflow between the two steps is what makes the transition run —
     without it the browser coalesces both style changes into one frame. */
  function reveal(el) {
    if (!el) return;
    window.clearTimeout(el.etHideTimer);
    el.hidden = false;
    void el.offsetHeight;
    el.classList.add('is-open');
  }

  function conceal(el) {
    if (!el) return;
    window.clearTimeout(el.etHideTimer);
    el.classList.remove('is-open');
    if (prefersReducedMotion()) {
      el.hidden = true;
      return;
    }
    el.etHideTimer = window.setTimeout(function () {
      el.hidden = true;
    }, TRANSITION_MS);
  }

  function focusables(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  // ----------------------------------------------------------------
  // Scroll lock — own class, ref-counted so nested opens behave
  // ----------------------------------------------------------------

  var lockCount = 0;

  function lockScroll() {
    lockCount += 1;
    document.documentElement.classList.add(SCROLL_LOCK_CLASS);
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
  }

  // ----------------------------------------------------------------
  // Desktop mega panels
  // ----------------------------------------------------------------

  /* Attribute selector rather than '#' + id: block ids are merchant-influenced and
     an id that is not a valid CSS identifier would make querySelector throw. */
  function byId(scope, id) {
    return id ? scope.querySelector('[id="' + id.replace(/"/g, '\\"') + '"]') : null;
  }

  function panelFor(root, toggle) {
    return byId(root, toggle.getAttribute('aria-controls'));
  }

  function closePanels(root, exceptToggle) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-et-header-toggle]'), function (toggle) {
      if (toggle === exceptToggle) return;
      if (toggle.getAttribute('aria-expanded') !== 'true') return;
      toggle.setAttribute('aria-expanded', 'false');
      conceal(panelFor(root, toggle));
    });

    if (!exceptToggle) {
      root.classList.remove('is-panel-open');
      conceal(root.querySelector('[data-et-header-scrim]'));
    }
  }

  function openPanel(root, toggle) {
    var panel = panelFor(root, toggle);
    if (!panel) return;
    closePanels(root, toggle);
    toggle.setAttribute('aria-expanded', 'true');
    reveal(panel);
    root.classList.add('is-panel-open');
    reveal(root.querySelector('[data-et-header-scrim]'));
  }

  function togglePanel(root, toggle) {
    if (toggle.getAttribute('aria-expanded') === 'true') {
      closePanels(root);
    } else {
      openPanel(root, toggle);
    }
  }

  // ----------------------------------------------------------------
  // Mobile drawer
  // ----------------------------------------------------------------

  function drawerFor(root) {
    var id = root.getAttribute('data-drawer-id');
    return id ? document.getElementById(id) : null;
  }

  /* The drawer does NOT open on an empty list. In the "featured" layout Liquid renders one item
     already expanded (`is_open` in snippets/et-header-drawer.liquid) and that item's cards are the
     first thing in the drawer - Figma 2183:39986. Recorded once, before any tap, because closing
     used to collapse EVERYTHING: the first open matched the design, and every open after it showed
     a bare list of links with the cards gone. */
  function snapshotDrawer(root) {
    var drawer = drawerFor(root);
    if (!drawer || drawer.etHeaderSnapshot) return;
    drawer.etHeaderSnapshot = true;

    Array.prototype.forEach.call(drawer.querySelectorAll('[data-et-header-drawer-toggle]'), function (toggle) {
      toggle.setAttribute(
        'data-et-header-drawer-initial',
        toggle.getAttribute('aria-expanded') === 'true' ? 'true' : 'false'
      );
    });
  }

  function openDrawer(root) {
    var drawer = drawerFor(root);
    if (!drawer || drawer.classList.contains('is-open')) return;

    closePanels(root);

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll();

    /* The section wrapper is z-index 10 and therefore its own stacking context,
       so a z-index:999 drawer inside it still loses to the announcement bar
       (z-index 20). Raising the wrapper for the duration is the fix. */
    var wrapper = root.closest('.shopify-section') || root.parentElement;
    if (wrapper) wrapper.classList.add('et-header--drawer-open');

    Array.prototype.forEach.call(root.querySelectorAll('[data-et-header-drawer-open]'), function (btn) {
      btn.setAttribute('aria-expanded', 'true');
    });

    var dialog = drawer.querySelector('[data-et-header-drawer-dialog]');
    if (dialog) {
      /* Focus the dialog itself rather than the first link: on a phone, focusing
         a link scrolls it into view and can trip the soft keyboard. */
      dialog.focus({ preventScroll: true });
    }
  }

  function closeDrawer(root, returnFocus) {
    var drawer = drawerFor(root);
    if (!drawer || !drawer.classList.contains('is-open')) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();

    /* Reset the drill-down, so that drilling into an item and closing does not leave the next open
       showing that one item with every other row hidden - which reads as a broken menu.

       Restores the state the SERVER rendered, not "everything collapsed": in the featured layout one
       item is expanded on purpose and its cards are the top of the drawer, so collapsing the lot
       emptied the menu from the second open onwards. snapshotDrawer() took that reading at bind
       time; a drawer that was never snapshotted falls back to closing the row, which is the old
       behaviour and correct for every non-featured layout. */
    Array.prototype.forEach.call(drawer.querySelectorAll('[data-et-header-drawer-toggle]'), function (toggle) {
      var want = toggle.getAttribute('data-et-header-drawer-initial') === 'true';
      if ((toggle.getAttribute('aria-expanded') === 'true') === want) return;
      toggle.setAttribute('aria-expanded', want ? 'true' : 'false');
      var sub = byId(document, toggle.getAttribute('aria-controls'));
      if (sub) sub.hidden = !want;
    });

    var body = drawer.querySelector('.et-header__drawer-body');
    if (body) body.scrollTop = 0;

    var wrapper = root.closest('.shopify-section') || root.parentElement;
    if (wrapper) wrapper.classList.remove('et-header--drawer-open');

    var opener = null;
    Array.prototype.forEach.call(root.querySelectorAll('[data-et-header-drawer-open]'), function (btn) {
      btn.setAttribute('aria-expanded', 'false');
      if (!opener) opener = btn;
    });

    if (returnFocus && opener) opener.focus();
  }

  function drawerIsOpen(root) {
    var drawer = drawerFor(root);
    return !!(drawer && drawer.classList.contains('is-open'));
  }

  function toggleDrawerSection(toggle) {
    var sub = byId(document, toggle.getAttribute('aria-controls'));
    if (!sub) return;

    var willOpen = toggle.getAttribute('aria-expanded') !== 'true';

    /* Exclusive: the drawer drills into ONE item at a time (see the drill-down rule in the CSS).
       Two open at once would hide each other's rows and strand the merchant. */
    if (willOpen) {
      var nav = toggle.closest('.et-header__drawer-nav');
      if (nav) {
        Array.prototype.forEach.call(nav.querySelectorAll('[data-et-header-drawer-toggle]'), function (other) {
          if (other === toggle) return;
          if (other.getAttribute('aria-expanded') !== 'true') return;
          other.setAttribute('aria-expanded', 'false');
          var otherSub = byId(document, other.getAttribute('aria-controls'));
          if (otherSub) otherSub.hidden = true;
        });
      }
    }

    toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    sub.hidden = !willOpen;

    /* Drilling in changes the panel's height dramatically; start from the top. */
    if (willOpen) {
      var body = toggle.closest('.et-header__drawer-body');
      if (body) body.scrollTop = 0;
    }
  }

  // ----------------------------------------------------------------
  // Wiring
  // ----------------------------------------------------------------

  function bind(root) {
    if (root.etHeaderBound) return;

    snapshotDrawer(root);

    var openTimer = null;
    var closeTimer = null;

    function cancelTimers() {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    }

    // --- click (works on every input type, and is the whole story on touch) ---
    var onClick = function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var opener = target.closest('[data-et-header-drawer-open]');
      if (opener && root.contains(opener)) {
        event.preventDefault();
        openDrawer(root);
        return;
      }

      var toggle = target.closest('[data-et-header-toggle]');
      if (toggle && root.contains(toggle)) {
        /* A toggle that is also a link keeps its destination. Where hover can open the panel the
           click simply navigates; on touch the first tap opens the panel (there is no other way to
           reach it) and a tap on the already-open label follows the link. */
        if (toggle.tagName === 'A' && toggle.getAttribute('href') &&
            (canHover() || toggle.getAttribute('aria-expanded') === 'true')) {
          cancelTimers();
          return;
        }
        event.preventDefault();
        cancelTimers();
        togglePanel(root, toggle);
        return;
      }

      /* The scrim is a child of root, so onDocumentPointerDown's containment check
         skips it, and the hover-intent close never runs on a coarse pointer. Without
         this a tap outside an open panel on a touch laptop/tablet does nothing. */
      if (target.closest('[data-et-header-scrim]')) {
        cancelTimers();
        closePanels(root);
      }
    };

    // --- hover intent, pointer devices only ---
    var onPointerOver = function (event) {
      if (!isDesktop() || !canHover()) return;
      var target = event.target;
      if (!target || !target.closest) return;

      window.clearTimeout(closeTimer);

      var toggle = target.closest('[data-et-header-toggle]');
      if (toggle && root.contains(toggle)) {
        window.clearTimeout(openTimer);
        if (toggle.getAttribute('aria-expanded') === 'true') return;
        openTimer = window.setTimeout(function () {
          openPanel(root, toggle);
        }, OPEN_DELAY);
        return;
      }

      /* Pointer is over the header but not over a trigger. If it is not over an
         open panel either, start closing — this is what makes moving sideways
         onto a plain link dismiss the panel. */
      if (!target.closest('[data-et-header-panel]')) {
        window.clearTimeout(openTimer);
        if (root.classList.contains('is-panel-open')) {
          closeTimer = window.setTimeout(function () {
            closePanels(root);
          }, CLOSE_DELAY);
        }
      }
    };

    var onPointerLeave = function () {
      if (!isDesktop() || !canHover()) return;
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(function () {
        closePanels(root);
      }, CLOSE_DELAY);
    };

    // --- keyboard + outside interaction ---
    var onFocusIn = function (event) {
      if (root.contains(event.target)) return;
      if (drawerIsOpen(root)) return;
      cancelTimers();
      closePanels(root);
    };

    var onDocumentPointerDown = function (event) {
      if (root.contains(event.target)) return;
      cancelTimers();
      closePanels(root);
    };

    var onKeyDown = function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;

      if (drawerIsOpen(root)) {
        closeDrawer(root, true);
        return;
      }

      var openToggle = root.querySelector('[data-et-header-toggle][aria-expanded="true"]');
      if (openToggle) {
        cancelTimers();
        closePanels(root);
        openToggle.focus();
      }
    };

    // --- drawer events (drawer is a sibling of the root, not a descendant) ---
    var drawer = drawerFor(root);
    var onDrawerClick = function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      if (target.closest('[data-et-header-drawer-close]')) {
        event.preventDefault();
        closeDrawer(root, true);
        return;
      }

      var sectionToggle = target.closest('[data-et-header-drawer-toggle]');
      if (sectionToggle) {
        event.preventDefault();
        toggleDrawerSection(sectionToggle);
      }
    };

    /* Minimal focus trap. Only Tab is intercepted; everything else (arrow keys
       inside the language listbox, typing) is left alone. */
    var onDrawerKeyDown = function (event) {
      if (event.key !== 'Tab') return;
      var dialog = drawer && drawer.querySelector('[data-et-header-drawer-dialog]');
      if (!dialog) return;

      var items = focusables(dialog);
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;

      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // --- viewport changes ---
    var onResize = function () {
      if (isDesktop()) {
        closeDrawer(root, false);
      } else {
        cancelTimers();
        closePanels(root);
      }
    };

    root.addEventListener('click', onClick);
    root.addEventListener('pointerover', onPointerOver);
    root.addEventListener('pointerleave', onPointerLeave);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('pointerdown', onDocumentPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);

    if (drawer) {
      drawer.addEventListener('click', onDrawerClick);
      drawer.addEventListener('keydown', onDrawerKeyDown);
    }

    /* Stored on the node so destroy() can detach every listener. Re-initialising
       a root that survived would otherwise double-bind and toggle twice per click. */
    root.etHeaderBound = {
      onClick: onClick,
      onPointerOver: onPointerOver,
      onPointerLeave: onPointerLeave,
      onFocusIn: onFocusIn,
      onDocumentPointerDown: onDocumentPointerDown,
      onKeyDown: onKeyDown,
      onResize: onResize,
      onDrawerClick: onDrawerClick,
      onDrawerKeyDown: onDrawerKeyDown,
      drawer: drawer,
      cancelTimers: cancelTimers
    };
  }

  function unbind(root) {
    var b = root.etHeaderBound;
    if (!b) return;

    b.cancelTimers();

    root.removeEventListener('click', b.onClick);
    root.removeEventListener('pointerover', b.onPointerOver);
    root.removeEventListener('pointerleave', b.onPointerLeave);
    document.removeEventListener('focusin', b.onFocusIn);
    document.removeEventListener('pointerdown', b.onDocumentPointerDown);
    document.removeEventListener('keydown', b.onKeyDown);
    window.removeEventListener('resize', b.onResize);

    if (b.drawer) {
      b.drawer.removeEventListener('click', b.onDrawerClick);
      b.drawer.removeEventListener('keydown', b.onDrawerKeyDown);
    }

    /* A section removed while its drawer was open would otherwise leave the
       page permanently unscrollable. */
    if (b.drawer && b.drawer.classList.contains('is-open')) unlockScroll();

    root.etHeaderBound = null;
  }

  function init(scope) {
    roots(scope).forEach(bind);
  }

  function destroy(scope) {
    roots(scope).forEach(unbind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // ----------------------------------------------------------------
  // Theme editor
  // ----------------------------------------------------------------

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });

  /* Without this, selecting a mega-menu block in the editor appears to do
     nothing — the merchant cannot preview the panel they are editing. */
  document.addEventListener('shopify:block:select', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var root = target.closest(ROOT_SELECTOR);

    if (root) {
      var panel = target.closest('[data-et-header-panel]');
      if (!panel) return;
      var toggle = root.querySelector(
        '[data-et-header-toggle][aria-controls="' + panel.id.replace(/"/g, '\\"') + '"]'
      );
      if (toggle) openPanel(root, toggle);
      return;
    }

    /* The drawer sits outside the root, so a block selected there needs handling of its own.
       Expanding the accordion is not enough: at mobile widths the drawer itself is closed, so the
       merchant selects a block in the editor and sees nothing happen at all. Open the drawer first,
       then expand. Guarded on viewport because above 1000px the drawer is display:none and opening
       it would only lock scroll for no visible reason. */
    var drawerEl = target.closest('[data-et-header-drawer]');
    var sub = target.closest('[data-et-header-drawer-sub]');
    if (!drawerEl && !sub) return;

    if (drawerEl && !isDesktop()) {
      var owner = document.querySelector('[data-drawer-id="' + drawerEl.id.replace(/"/g, '\\"') + '"]');
      if (owner) openDrawer(owner);
    }

    if (sub && sub.hidden) {
      var drawerToggle = document.querySelector(
        '[data-et-header-drawer-toggle][aria-controls="' + sub.id.replace(/"/g, '\\"') + '"]'
      );
      if (drawerToggle) toggleDrawerSection(drawerToggle);
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var root = target.closest(ROOT_SELECTOR);
    if (root) closePanels(root);
  });
})();
